// mission.schema.json source — projection `mission.json` shape (spec "Vòng
// đời mission và state machine" §5, example JSON block "mission.json — ví dụ
// projection đang chạy"). Zod is authoritative; generator (scripts/
// autopilot-schemas/generate-json-schemas.ts) derives the JSON Schema copy
// committed to team-ai-pack templates/autopilot/mission.schema.json.
//
// Gate status enum is `not_requested|pending|approved|waived|invalidated`
// per spec §"Gate, danh tính và tài nguyên" — `waived` only valid for G2.
import { z } from "zod";

const GateStatusSchema = z.enum(["not_requested", "pending", "approved", "waived", "invalidated"]);

// §"Gate, danh tính và tài nguyên": "Giá trị user_id minh họa phải resolve
// thật trước approval; không accept placeholder trong runtime." Placeholder
// pattern is the spec's own example shape, `<resolved-at-setup>` /
// `<mm-user-id>` — a literal angle-bracket token instead of a resolved id.
const PLACEHOLDER_ACTOR_ID_RE = /^<.*>$/;

const GateRecordSchema = z
  .object({
    status: GateStatusSchema,
    request_id: z.string().optional(),
    artifact_hash: z.string().optional(),
    actor_id: z.string().optional(),
    at: z.string().optional(),
    authority_ref: z.string().optional(),
    reason: z.string().optional(),
  })
  .strict()
  .refine(
    (gate) =>
      !(
        (gate.status === "approved" || gate.status === "waived") &&
        gate.actor_id !== undefined &&
        PLACEHOLDER_ACTOR_ID_RE.test(gate.actor_id)
      ),
    {
      message:
        "gate actor_id là placeholder chưa resolve — không được approved/waived với giá trị này",
    },
  );

export const MissionPhaseSchema = z.enum([
  "DRAFTING",
  "SURVEYING",
  "PLANNING",
  "DELIVERING",
  "TESTING",
  "FIXING",
  "REVIEWING",
  "DEPLOYING",
  "VERIFYING",
  "ROLLING_BACK",
  "DONE",
  "ABORTED",
]);

export const MissionExecutionStatusSchema = z.enum([
  "READY",
  "RUNNING",
  "WAITING_GATE",
  "WAITING_HUMAN",
  "SLEEPING",
  "PAUSED",
  "RECOVERING",
  "STOPPING",
  "TERMINAL",
]);

export const MissionSchema = z
  .object({
    schema_version: z.literal(1),
    contract_version: z.string().min(1),
    id: z.string().min(1),
    mode: z.enum(["greenfield", "brownfield"]),
    phase: MissionPhaseSchema,
    execution_status: MissionExecutionStatusSchema,
    last_event_seq: z.number().int().nonnegative(),
    revision: z.number().int().positive(),
    workspace_root: z.string().min(1),
    source_repo: z.string().min(1),
    mission_workspace: z.string().min(1),
    repo_root: z.string().min(1),
    branch: z.string().min(1),
    base_branch: z.string().min(1),
    base_sha: z.string().min(1),
    current_ticket: z.string().nullable(),
    current_run: z.string().nullable(),
    worker: z
      .object({
        pid: z.number().int().positive(),
        process_start: z.string().min(1),
        session_id: z.string().min(1),
      })
      .strict()
      .nullable(),
    wake_at: z.string().nullable(),
    wait_reasons: z.array(z.string()),
    resume_context: z.record(z.string(), z.unknown()).nullable(),
    artifacts: z.record(z.string(), z.unknown()),
    gates: z
      .object({
        g0: GateRecordSchema,
        g1: GateRecordSchema,
        g2: GateRecordSchema,
        g3: GateRecordSchema,
      })
      .strict(),
    counters: z
      .object({
        physical_runs: z.number().int().nonnegative(),
        counted_failures: z.number().int().nonnegative(),
      })
      .strict(),
    updated_at: z.string().min(1),
  })
  .strict();

export type Mission = z.infer<typeof MissionSchema>;
